import {
  PagedResponse,
  PatchWithMeta,
  QueryParams,
  ValidatedResponse,
} from "@ofqual/portal-core/core/types";
import {
  all,
  call,
  getContext,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { Qualification } from "../domain/Qualification";
import { MODULE_NAME } from "../moduleName";
import { qualificationService } from "../services";
import {
  ActionPayload,
  APPROVE_QUALIFICATION,
  CREATED_ITEM,
  CREATING_ITEM,
  CREATING_ITEM_VALIDATION_FAIL,
  loadedItem,
  loadedItems,
  loadingItem,
  LOADING_ITEM,
  LOADING_ITEMS,
  REJECT_QUALIFICATION,
  SEND_MESSAGE,
  UPDATING_ITEM,
  UPDATING_ITEM_VALIDATION_FAIL,
} from "./actions";
import { authSelector } from "./selectors";
import { History } from "history";
import { Attachment } from "../domain/Attachment";
import { refreshActivityFeed } from "@ofqual/portal-activity-feed";
import { ApproveQualification } from "../domain/ApproveQualification";
function* navigate(url: string) {
  const history: History = yield getContext("history");
  yield call(() => history.push(url));
}

function* watchItemsSaga() {
  yield takeLatest(LOADING_ITEMS, itemsSaga);
}

function* watchItemSaga() {
  yield takeLatest(LOADING_ITEM, itemSaga);
}

function* watchUpdateItemSaga() {
  yield takeEvery(UPDATING_ITEM, updateItemSaga);
}
function* watchCreateItemSaga() {
  yield takeEvery(CREATING_ITEM, createItemSaga);
}
function* watchSendMessageSaga() {
  yield takeEvery(SEND_MESSAGE, sendMessageSaga);
}
function* watchApproveQualificationSaga() {
  yield takeEvery(APPROVE_QUALIFICATION, approveQualification);
}
function* watchRejectQualificationSaga() {
  yield takeEvery(REJECT_QUALIFICATION, rejectQualification);
}

function* itemsSaga({ payload }: ActionPayload<QueryParams>) {
  const { token } = yield select(authSelector);

  const items: PagedResponse<Qualification> = yield call(
    [qualificationService, qualificationService.search],
    { ...payload },
    { token }
  );
  yield put(loadedItems(items));
}

function* itemSaga({ payload }: ActionPayload<string>) {
  const { token } = yield select(authSelector);

  const item: Qualification = yield call(
    [qualificationService, qualificationService.get],
    payload,
    {
      token,
    }
  );
  yield put(loadedItem(item));
}

function* updateItemSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { id, patchWithMeta, documentsToDelete, newDocuments } = payload as {
    id: string;
    patchWithMeta: PatchWithMeta<Qualification>;
    documentsToDelete: Attachment[];
    newDocuments: File[];
  };

    const deleteAttachmentsCalls = () =>
    Promise.all(
      documentsToDelete.map((doc) =>
        qualificationService.deleteAttachment(id, doc.blobId, {
          token,
        })
      ),
    );
  const attachmentsCall = () =>
      newDocuments && newDocuments.length
        ? qualificationService.postAttachments(
            id,
            newDocuments,
            {
              token,
            }
          )
        : Promise.resolve();

  const { validatedResponse } = yield all({
    validatedResponse: call(
      [qualificationService, qualificationService.update],
      id,
      patchWithMeta,
      {
        token,
      }
    ),
    deletedDocs: call(deleteAttachmentsCalls),
    newDocs: call(attachmentsCall),
  });

  if (validatedResponse.isValid) {
    yield put(loadingItem(id));
    yield put(refreshActivityFeed(id));
  } else {
    yield put({
      type: UPDATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* createItemSaga({ payload }: ActionPayload<Qualification>) {
  const { token } = yield select(authSelector);
  const validatedResponse: ValidatedResponse<Qualification> = yield call(
    [qualificationService, qualificationService.create],
    payload,
    {
      token,
    }
  );
  if (validatedResponse.isValid) {
    const id = validatedResponse.data.id;
    yield call(
      [qualificationService, qualificationService.postAttachments],
      id,
      payload.files,
      {
        token,
      }
    );
    yield put({ type: CREATED_ITEM });
    yield call(() => navigate(`/${MODULE_NAME}/${id}`));
  } else {
    yield put({
      type: CREATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* sendMessageSaga({
  payload,
}: ActionPayload<{ id: string; files: File[]; message: string }>) {
  const { token } = yield select(authSelector);
  const { id, files, message } = payload;
  const qualification: Qualification = yield call(
    [qualificationService, qualificationService.sendMessage],
    id,
    files,
    message,
    {
      token,
    }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(qualification));
}

function* approveQualification({
  payload,
}: ActionPayload<{
  id: string;
  approveQualification: ApproveQualification;
}>) {
  const { token } = yield select(authSelector);
  const { id, approveQualification } = payload;
  yield call(
    [qualificationService, qualificationService.approve],
    id,
    approveQualification,
    {
      token,
    }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadingItem(id));
}

function* rejectQualification({
  payload,
}: ActionPayload<{
  id: string;
  response: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, response } = payload;
  yield call(
    [qualificationService, qualificationService.reject],
    id,
    response,
    {
      token,
    }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadingItem(id));
}

export function* rootSaga() {
  yield all([
    watchItemsSaga(),
    watchItemSaga(),
    watchUpdateItemSaga(),
    watchCreateItemSaga(),
    watchSendMessageSaga(),
    watchApproveQualificationSaga(),
    watchRejectQualificationSaga()
  ]);
}
